import { GetLoggedUserUseCase } from "@authentication/domain/usecases/get-logged-user.usecase";
import { BeneficiariesSummary } from "@beneficiary/domain/models/beneficiaries-summary.model";
import { GetRecentModifiedBeneficiariesUseCase } from "@beneficiary/domain/usecases/get-recent-modified-beneficiaries.usecase";
import { Derivation } from "@catalogue/domain/models/derivation.model";
import { Service } from "@catalogue/domain/models/service.model";
import { GetAllDerivationsUseCase } from "@catalogue/domain/usecases/get-all-derivations.usecase";
import { GetAllServicesUseCase } from "@catalogue/domain/usecases/get-all-services.usecase";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { GetEmployeesCountByTypeUseCase } from "@entity/domain/usecases/employee/get-employees-count-by-type.usecase";
import { ProjectEmployeesCount } from "@project/domain/models/project-employees.model";
import { ProjectsSummary } from "@project/domain/models/projects-summary.model";
import { GetRecentsModifiedProjectsUseCase } from "@project/domain/usecases/get-recents-modified-projects.usecase";
import { injectable } from "inversify";
import { computed, flow, makeObservable, observable, runInAction } from "mobx";
import { Pagination } from "../../domain/models/pagination";

export interface ProjectListTable {
    id: number;
    name: string;
    financialEntitiesList: string;
    status: string;
}

interface ProjectsListTable {
    projects: ProjectListTable[];
    count: number;
}

export interface BeneficiaryListTable {
    id: number;
    name: string;
    documentNumber: string;
}

interface BeneficiariesListTable {
    beneficiaries: BeneficiaryListTable[];
    count: number;
}

export interface DerivationListTable {
    id: number;
    beneficiaryName: string;
    serviceCategoryName: string;
    entityName: string;
}

interface DerivationsListTable {
    derivations: DerivationListTable[];
    count: number;
}

@injectable()
export class HomePageViewModel extends BaseViewModel {
    @observable
    beneficiariesLoading = true;

    @observable
    projectsLoading = true;

    @observable
    servicesLoading = true;

    @observable
    derivationsLoading = true;

    @observable
    employeesLoading = true;

    @observable
    pagination: Pagination = Pagination.NoPagination();

    @observable
    employeesCount: ProjectEmployeesCount = new ProjectEmployeesCount(0, 0, 0);

    @observable
    private _projects: ProjectsSummary = new ProjectsSummary([], 0);

    @observable
    private _beneficiaries: BeneficiariesSummary = new BeneficiariesSummary(
        [],
        0,
    );

    @observable
    private _catalogueDerivations: Derivation[] = [];

    @observable
    private _derivationsTotal = 0;

    @observable
    private _services: Service[] = [];

    @computed
    get projectsTable(): ProjectsListTable {
        return {
            count: this._projects.count,
            projects: this._projects.projectsSummary.map((projectSummary) => ({
                id: projectSummary.id,
                name: projectSummary.name,
                financialEntitiesList:
                    projectSummary.financialEntityTypeLabels.join(", "),
                status: projectSummary.statusLabel,
            })),
        };
    }

    @computed
    get beneficiariesTable(): BeneficiariesListTable {
        return {
            count: this._beneficiaries.count,
            beneficiaries: this._beneficiaries.beneficiariesSummary.map(
                (beneficiarySummary) => ({
                    id: beneficiarySummary.id,
                    name: `${beneficiarySummary.name} ${beneficiarySummary.surname}`,
                    documentNumber: beneficiarySummary.documentNumber
                        ? beneficiarySummary.documentNumber
                        : "",
                }),
            ),
        };
    }

    @computed
    get projectsCount(): number {
        return this.projectsTable.count;
    }

    @computed
    get beneficiariesCount(): number {
        return this._beneficiaries.count;
    }

    @computed
    get derivationsCount(): number {
        return this._derivationsTotal;
    }

    @computed
    get servicesCount(): number {
        return this._services.length;
    }

    @computed
    get derivationsTable(): DerivationsListTable {
        return {
            count: this._catalogueDerivations.length,
            derivations: this._catalogueDerivations.map((derivation) => {
                const entityName = derivation.entity
                    ? derivation.entity.name
                    : String(derivation.entityId);

                return {
                    id: derivation.id,
                    beneficiaryName: `${derivation.beneficiary.name} ${derivation.beneficiary.surname}`,
                    serviceCategoryName: derivation.service.category,
                    entityName: entityName,
                };
            }),
        };
    }

    constructor(
        private getRecentModifiedBeneficiariesUseCase: GetRecentModifiedBeneficiariesUseCase,
        private getLoggedUserUseCase: GetLoggedUserUseCase,
        private getEmployeesCountByTypeUseCase: GetEmployeesCountByTypeUseCase,
        private getAllServicesUseCase: GetAllServicesUseCase,
        private getAllDerivationsUseCase: GetAllDerivationsUseCase,
        private getRecentsModifiedProjectsUseCase: GetRecentsModifiedProjectsUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        void this.searchBeneficiariesBy();
        void this.searchProjectsBy();
        void this.getEmployeesCount();
        void this.searchServices();
        void this.searchDerivations();
    }

    @flow
    async *searchBeneficiariesBy(): AsyncGenerator<void> {
        try {
            const beneficiaries =
                await this.getRecentModifiedBeneficiariesUseCase.execute();

            runInAction(() => {
                this._beneficiaries = beneficiaries;
            });
        } finally {
            runInAction(() => {
                this.beneficiariesLoading = false;
            });
        }
    }

    @flow
    async *searchProjectsBy(): AsyncGenerator<void> {
        try {
            const projects =
                await this.getRecentsModifiedProjectsUseCase.execute();
            runInAction(() => {
                this._projects = projects;
            });
        } finally {
            runInAction(() => {
                this.projectsLoading = false;
            });
        }
    }

    @flow
    async *getEmployeesCount(): AsyncGenerator<void> {
        try {
            const employeesCount =
                await this.getEmployeesCountByTypeUseCase.execute();

            if (employeesCount !== null) {
                runInAction(() => {
                    this.employeesCount = employeesCount;
                });
            }
        } finally {
            runInAction(() => {
                this.employeesLoading = false;
            });
        }
    }

    @flow
    async *searchServices(): AsyncGenerator<void> {
        try {
            const currentUser = this.getLoggedUserUseCase.execute();
            const servicesResponse = await this.getAllServicesUseCase.execute({
                entityId: currentUser?.entityId ?? 0,
            });

            runInAction(() => {
                this._services = servicesResponse.items;
            });
        } finally {
            runInAction(() => {
                this.servicesLoading = false;
            });
        }
    }

    @flow
    async *searchDerivations(): AsyncGenerator<void> {
        try {
            const currentUser = this.getLoggedUserUseCase.execute();
            const recentDerivations = 5;

            const requestFilters: {
                assigned_by?: number;
                ordering?: string;
            } = {
                ordering: "-created_at",
            };
            if (currentUser.entityId !== undefined) {
                requestFilters.assigned_by = currentUser.entityId;
            }

            const derivationsResponse =
                await this.getAllDerivationsUseCase.execute(
                    requestFilters,
                    new Pagination(1, recentDerivations),
                );

            runInAction(() => {
                this._catalogueDerivations = derivationsResponse.items;
                this._derivationsTotal = derivationsResponse.total;
            });
        } finally {
            runInAction(() => {
                this.derivationsLoading = false;
            });
        }
    }
}
